import classNames from 'classnames';
import React, { CSSProperties, useState } from 'react';
import styles from './Dropdown.module.scss';

type BaseDropdownProps<T> = {
  label: string;
  allValues: T[];
  availableValues: T[];
  style?: CSSProperties;
};

type InternalStateDropdownProps<T> = BaseDropdownProps<T> & {
  type: 'internal';
  initialValue?: T;
  onChange: (value: T) => void;
};

type ExternalStateDropdownProps<T> = BaseDropdownProps<T> & {
  type: 'external';
  value: T | undefined;
  setValue: (newValue: T) => void;
};

export type DropdownProps<T> = InternalStateDropdownProps<T> | ExternalStateDropdownProps<T>;

export function Dropdown<T>(props: DropdownProps<T>): React.ReactElement {
  const initialValue = props.type === 'external' ? props.value : props.initialValue;
  const [localValue, setLocalValue] = useState<T | undefined>(initialValue);

  if (props.type === 'internal') {
    const { type, initialValue: _, onChange, ...rest } = props;

    const handleChange = (newValue: T) => {
      setLocalValue(newValue);
      props.onChange(newValue);
    };

    return <_Dropdown type="external" {...rest} value={localValue} setValue={handleChange} />;
  } else {
    return <_Dropdown {...props} />;
  }
}

function _Dropdown<T>({ style, value, setValue, allValues, availableValues, label }: ExternalStateDropdownProps<T>): React.ReactElement {
  return (
    <select
      style={style}
      value={String(value ?? '')}
      className={classNames(styles.dropdown, { [styles.empty]: !value })}
      onChange={(e) => setValue(e.target.value as unknown as T)}
    >
      {!value && (
        <option value="" disabled>
          {value ?? label}
        </option>
      )}
      {allValues.map((s, i) => {
        const isAvailable = availableValues.includes(s);
        return (
          <option key={`option-${i}`} value={isAvailable ? String(s) : ''} disabled={!isAvailable}>
            {s}
            {isAvailable ? '' : ' - Sold Out'}
          </option>
        );
      })}
    </select>
  );
}
